<template>
    <v-card class="svg-preview" :loading="isLoading">
        <v-card-text class="pa-4 card-content" :style="`width:100%;aspect-ratio: calc(${width} / ${height});`">
            <div ref="wordCloud" class="wordCloud"></div>
            <v-skeleton-loader
                type="image"
                 v-if="isLoading && !drawed"
            ></v-skeleton-loader>
        </v-card-text>
        <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn @click="save" :loading="isLoading">Скачать SVG</v-btn>
        </v-card-actions>
    </v-card>
</template>
<script>
import * as d3 from 'd3';
import cloud from 'd3-cloud';
import _ from "lodash";

export default {
    name: 'WordCloud',
    props: {
        data: {
            type: Array,
            required: true
        },
        fontSizeMapper: {
            type: Function,
            default: (d) => Math.sqrt(d.value),
        },
        onWordClick: {
            type: Function,
            default: () => {
            },
        },
        rotate: {
            type: [Function, Array, String, Number],
            default: 0
        },
        font: {
            type: [String, Function],
            default: 'Impact'
        },
        fontContent: {
            type: String,
            default: ''
        },
        weight: {
            type: [Number, String],
            default: 500
        },
        width: {
            type: [Number, String],
            default: 700
        },
        height: {
            type: [Number, String],
            default: 500
        },
        padding: {
            type: [Number, String],
            default: 5 // Увеличиваем отступы между словами
        },
        spiral: {
            type: String,
            default: 'archimedean'
        },
        coloring: {
            type: String,
            default: 'random'
        },
        colors: {
            type: Array
        },
        backgroundImage: {
            type: String,
            default: ''
        },
        backgroundImageOpacity: {
            type: Number,
            default: 1.00
        },
        fontScale: {
            type: Number,
            default: 1
        },
        margin: {
            type: Object,
            default: () => ({top: 0, right: 0, bottom: 0, left: 0})
        },
        loading: {
            type: Boolean,
        },
    },
    data: () => ({
        svg: null,
        loadedFonts: [],
        fontCssPrefix: 'css-cache-font-',
        loadingLocal: true,
        drawed: false,
    }),
    mounted() {
        this.cloud = cloud()
            .timeInterval(15)   // 15ms - эмпирически, чем меньше тем "плавнее", но суммарный процесс генерации облака дольше.
            .on('end', this.draw);
        this.updateCloud();
    },
    computed: {
        updateCloudDebounced() {
            return _.debounce(this.updateCloud, 1000);
        },
        isLoading() {
            return this.loading || this.loadingLocal;
        },
    },
    watch: {
        data() {
            this.updateCloudDebounced();
        },
        rotate() {
            this.updateCloudDebounced();
        },
        font() {
            this.updateCloudDebounced();
        },
        padding() {
            this.updateCloudDebounced();
        },
        spiral() {
            this.updateCloudDebounced();
        },
        colors() {
            this.updateCloudDebounced();
        },
        coloring() {
            this.updateCloudDebounced();
        },
        backgroundImage() {
            this.updateCloudDebounced();
        },
        backgroundImageOpacity() {
            this.updateCloudDebounced();
        },
        fontScale() {
            this.updateCloudDebounced();
        },
        fontSizeMapper() {
            this.updateCloudDebounced();
        },
        margin: {
            handler() {
                this.updateCloudDebounced();
            },
            deep: true,
        },
        fontContent: {
            handler(v) {
                if (!v) {
                    return;
                }
                this.addOrUpdateCss(v, this.fontCssPrefix + this.font);
                //setTimeout(() => {
                this.updateCloudDebounced();
                //}, 500);
            },
            immediate: true,
        },
    },
    beforeDestroy() {
        this.cloud.stop();
        this.loadedFonts.forEach(v => this.removeCss(v));
        this.loadedFonts = [];
    },
    methods: {
        getRotate(rotate, item) {
            rotate = rotate || this.rotate;
            if (Array.isArray(rotate)) {
                return !rotate.length ? 0 : rotate[Math.floor(Math.random() * rotate.length)];
            } else if (typeof rotate === 'function') {
                return rotate(item);
            }
            return Number(rotate) || 0;
        },
        removeCss(id) {
            const style = document.getElementById(id);
            style?.parentNode?.removeChild(style);
        },
        addOrUpdateCss(v, id) {
            let style = document.getElementById(id);
            if (!style) {
                style = document.createElement('style');
                style.type = 'text/css';
                style.id = id;
                this.loadedFonts.push(id);
                document.head.appendChild(style);
            }
            try {
                style.innerHTML = v;
            }
                //IE fix
            catch (error) {
                style.styleSheet.cssText = v;
            }
        },
        updateCloud() {
            if (this.loading) {
                return;
            }
            this.loadingLocal = true;
            const words = this.data.map(d => ({...d}));
            if (this.colors) {
                this.fill = d3.scaleOrdinal(this.colors);
            } else {
                this.fill = d3.scaleOrdinal(d3.schemeCategory10);
            }
            this.cloud
                .stop()
                .size([+this.width - (+this.margin.left + +this.margin.right), +this.height - (+this.margin.top + +this.margin.bottom)]) // Adjust size by margins
                .words(words)
                .padding(+this.padding) // Конвертируем padding в число
                .spiral(this.spiral)
                .rotate(item => this.getRotate(this.rotate, item))
                .font(this.font)
                .fontSize(d => (this.fontSizeMapper(d) * +this.fontScale)) // Конвертируем fontScale в число
                .start()
                .on('end', words => {
                    this.draw(words);
                    this.$nextTick(() => {
                        this.drawed = true;
                    });
                })
            ;
        },
        draw(words) {
            const margin = {
                top: +this.margin.top, // Конвертируем margin в числа
                right: +this.margin.right,
                bottom: +this.margin.bottom,
                left: +this.margin.left
            };
            const width = +this.width - margin.left - margin.right;
            const height = +this.height - margin.top - margin.bottom;

            d3.select(this.$refs.wordCloud).selectAll('*').remove();
            this.svg = d3.select(this.$refs.wordCloud).append('svg');

            this.svg.attr('xmlns', 'http://www.w3.org/2000/svg') // Добавляем xmlns атрибут
                .attr('xmlns:xlink', 'http://www.w3.org/1999/xlink') // Добавляем xmlns:xlink атрибут
                .attr('viewBox', `0 0 ${+this.width} ${+this.height}`) // Устанавливаем viewBox
                .attr('font-family', this.font)
                .attr('text-anchor', 'middle')
                .attr('style', 'max-width: 100%; height: auto; height: intrinsic;');

            // this.svg.append('title').text('Word Cloud'); // Добавляем элемент title

            if (this.fontContent?.length) {
                let defs = this.svg.append('defs');
                defs.append('style').attr("type", "text/css").text(this.fontContent);
            }

            if (this.backgroundImage) {
                this.svg.append('image')
                    .attr('href', this.backgroundImage) // Changed xlink:href to href
                    .attr('width', width + margin.left + margin.right)
                    .attr('height', height + margin.top + margin.bottom)
                    .attr('x', 0)
                    .attr('y', 0)
                    // .attr('filter', 'url(#brightness)')
                    .attr('opacity', this.backgroundImageOpacity?.toString())
                    .attr('preserveAspectRatio', 'xMidYMid slice'); // Добавляем атрибут preserveAspectRatio
            }

            const g = this.svg.append('g')
                .attr('transform', `translate(${margin.left + width / 2}, ${margin.top + height / 2})`);

            g.selectAll('text')
                .data(words)
                .enter().append('text')
                .style('font-family', this.font)
                .style('font-size', d => `${d.size}px`) // Размер шрифта зависит от значения
                .style('font-weight', this.weight) // Делаем шрифт жирным
                .style('fill', (d, i) => this.fill(i))
                .attr('text-anchor', 'middle')
                .attr('transform', d => `translate(${d.x},${d.y}) rotate(${d.rotate})`)
                .text(d => d.text)
                .on('click', this.onWordClick);
            this.loadingLocal = false;
        },
        save() {
            const svgData = `<?xml version="1.0" encoding="UTF-8"?>${this.svg.node().outerHTML}`;
            const svgBlob = new Blob([svgData], {type: "image/svg+xml;charset=utf-8"});
            const svgUrl = URL.createObjectURL(svgBlob);
            const downloadLink = document.createElement("a");
            downloadLink.href = svgUrl;
            downloadLink.download = "wordcloud.svg";
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        },
    }
};
</script>
<style scoped lang="scss">
.wordCloud {
    g {
        text {
            cursor: pointer;
        }
    }
}
.card-content {
    position: relative;
}
::v-deep .v-skeleton-loader {
    position: absolute;
    width:100%;
    height: 100%;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    z-index: 999;
}
::v-deep .v-skeleton-loader__bone {
    width: 100%;
    height: 100%;
    flex: 1;
}
</style>
